import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
  Tooltip,
  useTheme,
  ClickAwayListener,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
// eslint-disable-next-line no-unused-vars
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTranslation } from "react-i18next";
import { tokens } from "../theme";
import { BRAND } from "./marketing/landing/brandTokens";
import { PANEL } from "../design-system/panel/panelTokens";
import {
  panelDropdownTriggerSx,
  panelTopbarActionIconSx,
  panelDropdownPopoverSlotProps,
  panelDropdownPopoverModalProps,
  panelDropdownMenuItemSx,
  panelDropdownMenuItemBadgeSx,
} from "../design-system/panel/panelDropdownSx";

const LANGUAGE_OPTIONS = [
  { code: "tr", short: "TR", labelKey: "languageNameTr" },
  { code: "en", short: "EN", labelKey: "languageNameEn" },
  { code: "fr", short: "FR", labelKey: "languageNameFr" },
];

const MARKETING_DARK = {
  teal: "#1DB8A0",
  tealGlow: "rgba(29, 184, 160, 0.22)",
  border: "rgba(255, 255, 255, 0.14)",
  panel: "rgba(12, 20, 36, 0.96)",
};

function normalizeLang(lng) {
  return (lng || "en").split("-")[0].toLowerCase();
}

const LanguageSelector = ({ darkMode = null, variant = null, size = "medium" }) => {
  const { i18n, t } = useTranslation("common");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [anchorEl, setAnchorEl] = useState(null);
  const [justClosed, setJustClosed] = useState(false);
  const open = Boolean(anchorEl);

  const activeCode = normalizeLang(i18n.language);

  const getLanguageLabel = (labelKey) => t(labelKey);

  const isMarketingLight = variant === "marketing";
  const isPanel = variant === "panel";
  const isDark =
    !isMarketingLight &&
    !isPanel &&
    (darkMode === true || (darkMode === null && theme.palette.mode === "dark"));
  const isCompact = size === "small";
  const height = isCompact ? 34 : 38;

  const handleOpen = (event) => {
    if (justClosed) {
      setJustClosed(false);
      return;
    }
    if (open) {
      handleClose();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  
  const handleClose = () => {
    setAnchorEl(null);
    setJustClosed(true);
    setTimeout(() => {
      setJustClosed(false);
    }, 150);
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem("languageManuallySet", "true");
    handleClose();
  };

  const triggerSx = isPanel
    ? panelDropdownTriggerSx
    : isMarketingLight
    ? {
        height,
        width: height,
        p: 0,
        minWidth: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${BRAND.borderLight}`,
        background: open ? BRAND.tealLight : "#FFFFFF",
        boxShadow: open
          ? `0 0 0 2px ${BRAND.tealGlow}, 0 4px 16px rgba(10, 22, 40, 0.08)`
          : "0 2px 8px rgba(10, 22, 40, 0.06)",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: `${BRAND.teal}88`,
          background: BRAND.tealLight,
        },
      }
    : isDark
    ? {
        height,
        width: height,
        p: 0,
        minWidth: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${MARKETING_DARK.border}`,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
        backdropFilter: "blur(12px)",
        boxShadow: open
          ? `0 0 0 2px ${MARKETING_DARK.tealGlow}, 0 8px 24px rgba(0,0,0,0.35)`
          : "0 4px 16px rgba(0,0,0,0.2)",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: MARKETING_DARK.teal,
          background: "rgba(29, 184, 160, 0.12)",
        },
      }
    : {
        height,
        width: height,
        p: 0,
        minWidth: 0,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${colors.blueAccent[500]}35`,
        background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`,
        boxShadow: open
          ? `0 0 0 2px ${colors.blueAccent[500]}40`
          : "0 4px 14px rgba(0,0,0,0.12)",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: colors.blueAccent[400],
          background: colors.primary[500],
        },
      };

  const panelSx = isPanel
    ? {
        mt: 1,
        p: 1,
        minWidth: 200,
        borderRadius: "12px",
        border: `1px solid ${PANEL.border}`,
        background: PANEL.surface,
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      }
    : isMarketingLight
    ? {
        mt: 1.25,
        p: 1,
        minWidth: 200,
        borderRadius: "16px",
        border: `1px solid ${BRAND.borderLight}`,
        background: "#FFFFFF",
        boxShadow: "0 16px 40px rgba(10, 22, 40, 0.12)",
      }
    : isDark
    ? {
        mt: 1.25,
        p: 1,
        minWidth: 200,
        borderRadius: "16px",
        border: `1px solid ${MARKETING_DARK.border}`,
        background: MARKETING_DARK.panel,
        backdropFilter: "blur(24px) saturate(160%)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
      }
    : {
        mt: 1.25,
        p: 1,
        minWidth: 200,
        borderRadius: "16px",
        border: `1px solid ${colors.blueAccent[500]}30`,
        background: colors.primary[400],
        boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
      };

  return (
    <>
      <Tooltip title={t("changeLanguage")} arrow>
        {isPanel ? (
          <IconButton
            onClick={handleOpen}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={t("changeLanguage")}
            sx={triggerSx}
          >
            <LanguageOutlinedIcon sx={panelTopbarActionIconSx} />
          </IconButton>
        ) : (
        <Button
          onClick={handleOpen}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t("changeLanguage")}
          sx={triggerSx}
        >
          <LanguageOutlinedIcon
            sx={{
              fontSize: isCompact ? 18 : 20,
              color: isMarketingLight
                ? BRAND.teal
                : isDark
                  ? MARKETING_DARK.teal
                  : colors.blueAccent[300],
            }}
          />
        </Button>
        )}
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        {...(isPanel ? panelDropdownPopoverModalProps : { disableScrollLock: true, BackdropProps: { invisible: true } })}
        slotProps={isPanel ? panelDropdownPopoverSlotProps(220) : undefined}
        sx={isPanel ? undefined : { pointerEvents: "none" }}
        PaperProps={isPanel ? undefined : { sx: { pointerEvents: "auto", ...panelSx } }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={{ width: "100%", height: "100%", p: isPanel ? 1 : 0 }}>
            <Typography
              sx={{
                px: 1.25,
                pt: 0.5,
                pb: 1,
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: isPanel
                  ? PANEL.text.label
                  : isMarketingLight
                  ? BRAND.textSoft
                  : isDark
                    ? "rgba(255,255,255,0.45)"
                    : colors.grey[400],
              }}
            >
              {t("changeLanguage")}
            </Typography>

            <Box component="ul" role="listbox" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {LANGUAGE_OPTIONS.map((language) => {
                const selected = activeCode === language.code;
                return (
                  <Box
                    component="li"
                    key={language.code}
                    role="option"
                    aria-selected={selected}
                  >
                    <Button
                      fullWidth
                      onClick={() => handleLanguageChange(language.code)}
                      sx={
                        isPanel
                          ? panelDropdownMenuItemSx(selected)
                          : {
                        justifyContent: "flex-start",
                        gap: 1.25,
                        px: 1.25,
                        py: 1.1,
                        borderRadius: "12px",
                        textTransform: "none",
                        color: isMarketingLight
                          ? BRAND.navy
                          : isDark
                            ? "#fff"
                            : colors.grey[100],
                        background: selected
                          ? isMarketingLight
                            ? BRAND.tealLight
                            : isDark
                              ? "rgba(29, 184, 160, 0.18)"
                              : `${colors.blueAccent[700]}35`
                          : "transparent",
                        border: selected
                          ? `1px solid ${
                              isMarketingLight
                                ? `${BRAND.teal}55`
                                : isDark
                                  ? MARKETING_DARK.teal
                                  : colors.blueAccent[500]
                            }55`
                          : "1px solid transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: isMarketingLight
                            ? BRAND.sectionBgMuted
                            : isDark
                              ? "rgba(255,255,255,0.06)"
                              : `${colors.blueAccent[700]}22`,
                        },
                      }}
                    >
                      <Box
                        sx={
                          isPanel
                            ? panelDropdownMenuItemBadgeSx(selected)
                            : {
                          width: 32,
                          height: 32,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontSize: "0.7rem",
                          fontWeight: 800,
                          letterSpacing: "0.04em",
                          color: selected
                            ? isMarketingLight
                              ? BRAND.tealDark
                              : isDark
                                ? MARKETING_DARK.teal
                                : colors.blueAccent[300]
                            : isMarketingLight
                              ? BRAND.textSecondary
                              : isDark
                                ? "rgba(255,255,255,0.7)"
                                : colors.grey[300],
                          background: selected
                            ? isMarketingLight
                              ? "rgba(29, 184, 160, 0.15)"
                              : isDark
                                ? "rgba(29, 184, 160, 0.2)"
                                : `${colors.blueAccent[800]}50`
                            : isMarketingLight
                              ? BRAND.sectionBgMuted
                              : isDark
                                ? "rgba(255,255,255,0.06)"
                                : `${colors.primary[500]}80`,
                          border: `1px solid ${
                            selected
                              ? isMarketingLight
                                ? `${BRAND.teal}55`
                                : isDark
                                  ? `${MARKETING_DARK.teal}55`
                                  : `${colors.blueAccent[500]}50`
                              : isMarketingLight
                                ? BRAND.borderLight
                                : isDark
                                  ? MARKETING_DARK.border
                                  : `${colors.blueAccent[700]}40`
                          }`,
                        }}
                      >
                        {language.short}
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          textAlign: "left",
                          fontWeight: selected ? 700 : 500,
                          fontSize: "0.9rem",
                        }}
                      >
                        {getLanguageLabel(language.labelKey)}
                      </Typography>
                      {selected && (
                        <CheckRoundedIcon
                          sx={{
                            fontSize: 20,
                            color: isPanel
                              ? PANEL.accent.main
                              : isMarketingLight
                              ? BRAND.teal
                              : isDark
                                ? MARKETING_DARK.teal
                                : colors.greenAccent[400],
                          }}
                        />
                      )}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popover>
    </>
  );
};

export default LanguageSelector;
